/**
 * Statistiques en direct (admin) — migration 0697 : les chiffres clés calculés à la demande (rechargés toutes
 * les 30 s), leur évolution par rapport à la période d'avant, une courbe par chiffre et les pages les plus vues.
 * Robots et comptes internes exclus, comme le récap journalier du Feed.
 */
import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { useAuth, isAdmin } from '@/lib/auth';
import { StatsChart } from '@/components/admin/stats-chart';
import { TrendBadge } from '@/components/admin/trend-badge';
import { PageName } from '@/components/admin/page-name';
import { FunnelPanel } from '@/components/admin/funnel-panel';
import { useFunnelStats } from '@/lib/admin/funnel-stats';
import { ConversionPanel } from '@/components/admin/conversion-panel';
import { useConversionFunnels } from '@/lib/admin/conversion-funnels';
import {
  STATS_METRICS,
  STATS_RANGES,
  STATS_WINDOWS,
  botRows,
  bucketLabel,
  bucketTitle,
  formatMetric,
  metricSeries,
  pageLabel,
  useLiveStats,
  type LiveStats,
  type MetricKey,
  type StatsRange,
  type TopPage,
} from '@/lib/admin/live-stats';

const TIME = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const WINDOW_METRICS: MetricKey[] = ['visitors', 'views', 'signups', 'active'];

function WindowCard({
  stats,
  id,
  label,
  vs,
}: {
  stats: LiveStats;
  id: keyof LiveStats['windows'];
  label: string;
  vs: string;
}) {
  const w = stats.windows?.[id];
  if (!w) return null;
  return (
    <section
      className="border-ink-200 rounded-2xl border bg-white p-4"
      aria-label={label}
      data-testid={`window-${id}`}
    >
      <h3 className="font-display text-ink-900 text-sm font-bold">{label}</h3>
      <p className="text-ink-400 mb-3 text-xs">{vs}</p>
      <dl className="grid grid-cols-2 gap-3">
        {WINDOW_METRICS.map((k) => {
          const m = STATS_METRICS.find((x) => x.key === k)!;
          return (
            <div key={k}>
              <dt className="text-ink-500 text-xs">{m.label}</dt>
              <dd className="flex flex-wrap items-baseline gap-1.5">
                <span className="font-display text-ink-900 text-xl font-bold tabular-nums">
                  {formatMetric(k, w.cur[k])}
                </span>
                <TrendBadge cur={w.cur[k]} prev={w.prev[k]} />
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

function TopPages({ title, app, pages }: { title: string; app: 'site' | 'app'; pages: TopPage[] }) {
  const max = Math.max(1, ...pages.map((p) => p.views));
  return (
    <section className="border-ink-200 rounded-2xl border bg-white p-4">
      <h3 className="font-display text-ink-900 mb-3 text-sm font-bold">{title}</h3>
      {pages.length === 0 ? (
        <p className="text-ink-400 text-sm">Aucune page vue sur la période.</p>
      ) : (
        <ol className="flex flex-col gap-2">
          {pages.map((p) => (
            <li key={p.path} className="text-sm">
              <div className="flex items-center justify-between gap-2">
                <PageName app={app} path={p.path} label={p.label} />
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className="text-ink-900 font-semibold tabular-nums">
                    {formatMetric('views', p.views)}
                  </span>
                  <TrendBadge cur={p.views} prev={p.prev_views} />
                </span>
              </div>
              <div className="bg-ink-50 mt-1 h-1.5 overflow-hidden rounded-full" aria-hidden>
                <div
                  className="bg-primary-500 h-full rounded-full"
                  style={{ width: `${(p.views / max) * 100}%` }}
                />
              </div>
              <p className="text-ink-400 mt-0.5 text-xs">
                {formatMetric('visitors', p.visitors)} visiteur{p.visitors > 1 ? 's' : ''}
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

const PLATFORM_LABELS: { key: 'site' | 'app_web' | 'ios' | 'android'; label: string }[] = [
  { key: 'site', label: 'Site' },
  { key: 'app_web', label: 'App (web)' },
  { key: 'ios', label: 'App iPhone' },
  { key: 'android', label: 'App Android' },
];

export function TutorAdminStatsPage() {
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const [range, setRange] = useState<StatsRange>('day');
  const { data, isLoading, isError, isFetching, refetch } = useLiveStats(range, admin);
  const funnel = useFunnelStats(range, admin);
  const conversions = useConversionFunnels(range, admin);

  if (!admin)
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-6">
        <p className="text-ink-500 text-sm">Réservé aux admins.</p>
      </div>
    );

  const rangeInfo = STATS_RANGES.find((r) => r.id === range)!;

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:py-8">
      <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="font-display text-ink-900 text-[24px] font-bold">
            Statistiques en direct
          </h1>
          <p className="text-ink-500 text-sm">
            Robots et comptes internes exclus. Rechargé toutes les 30 secondes
            {data ? ` · à jour à ${TIME.format(new Date(data.generated_at))}` : ''}.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            void refetch();
            void funnel.refetch();
            void conversions.refetch();
          }}
          disabled={isFetching}
          className="text-ink-700 border-ink-200 inline-flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border bg-white px-4 text-sm font-semibold active:scale-[0.98] disabled:opacity-60 sm:w-auto"
        >
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} aria-hidden />
          Actualiser
        </button>
      </header>

      {isError && !data && (
        <p className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700" role="alert">
          Les statistiques n’ont pas pu être chargées. Réessaie dans un instant.
        </p>
      )}
      {isLoading && !data && <p className="text-ink-500 text-sm">Chargement…</p>}

      {data && (
        <div className="flex flex-col gap-6">
          <section
            className="border-primary-200 bg-primary-50 flex flex-col gap-2 rounded-2xl border p-4"
            aria-label="En ce moment"
          >
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5" aria-hidden>
                <span className="bg-primary-400 absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 motion-reduce:animate-none" />
                <span className="bg-primary-500 relative inline-flex h-2.5 w-2.5 rounded-full" />
              </span>
              <h2 className="font-display text-ink-900 text-sm font-bold">En ce moment</h2>
            </div>
            <p className="text-ink-800 text-sm">
              <strong
                className="font-display text-ink-900 text-2xl tabular-nums"
                data-testid="visitors-now"
              >
                {formatMetric('visitors', data.now.visitors_5min)}
              </strong>{' '}
              visiteur{data.now.visitors_5min > 1 ? 's' : ''} ces 5 dernières minutes
            </p>
            {data.now.pages_5min.length > 0 && (
              <ul className="text-ink-600 flex flex-col gap-0.5 text-xs">
                {data.now.pages_5min.map((p) => (
                  <li key={`${p.app}${p.path}`} className="flex justify-between gap-2">
                    <span className="min-w-0 truncate">
                      {p.app === 'site' ? 'Site' : 'App'} · {pageLabel(p.path, p.label)}
                    </span>
                    <span className="shrink-0 tabular-nums">{p.visitors}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-label="Chiffres clés">
            <h2 className="font-display text-ink-900 mb-3 text-lg font-bold">Chiffres clés</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {STATS_WINDOWS.map((w) => (
                <WindowCard key={w.id} stats={data} id={w.id} label={w.label} vs={w.vs} />
              ))}
            </div>
          </section>

          <section aria-label="Courbes">
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="font-display text-ink-900 text-lg font-bold">Courbes</h2>
              <div role="tablist" aria-label="Période" className="flex gap-2">
                {STATS_RANGES.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    role="tab"
                    aria-selected={range === r.id}
                    onClick={() => setRange(r.id)}
                    className={cn(
                      'min-h-[2.75rem] flex-1 rounded-xl border px-4 text-sm font-semibold sm:flex-none',
                      range === r.id
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-ink-200 text-ink-600 bg-white',
                    )}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-ink-500 mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="inline-flex items-center gap-1.5">
                <span className="bg-primary-500 h-0.5 w-5 rounded-full" aria-hidden />
                {rangeInfo.id === 'day' ? 'Les 24 dernières heures' : `Les ${rangeInfo.label}`}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="border-ink-300 w-5 border-t-2 border-dashed" aria-hidden />
                {rangeInfo.prevLabel}
              </span>
            </p>
            <div
              className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', isFetching && 'opacity-80')}
            >
              {STATS_METRICS.map((m) => {
                const s = metricSeries(data.series, m.key);
                const cur = data.range_totals.cur[m.key];
                const prev = data.range_totals.prev[m.key];
                return (
                  <section
                    key={m.key}
                    className="border-ink-200 rounded-2xl border bg-white p-4"
                    aria-label={m.label}
                    data-testid={`chart-${m.key}`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="font-display text-ink-900 text-sm font-bold">{m.label}</h3>
                      <span className="flex items-baseline gap-1.5">
                        <span className="font-display text-ink-900 text-xl font-bold tabular-nums">
                          {formatMetric(m.key, cur)}
                        </span>
                        <TrendBadge cur={cur} prev={prev} />
                      </span>
                    </div>
                    <p className="text-ink-400 mb-2 text-xs">
                      {m.help} Avant : {formatMetric(m.key, prev)}.
                    </p>
                    <StatsChart
                      label={m.label}
                      cur={s.cur}
                      prev={s.prev}
                      ticks={data.series.map((p) => bucketLabel(p.t, data.bucket))}
                      titles={data.series.map((p) => bucketTitle(p.t, data.bucket))}
                      format={(v) => formatMetric(m.key, v)}
                      prevLabel="Avant"
                    />
                  </section>
                );
              })}
            </div>
          </section>

          {conversions.data ? (
            <ConversionPanel funnels={conversions.data.funnels} rangeLabel={rangeInfo.label} />
          ) : conversions.isError ? (
            <p className="text-sm text-red-600">
              Les taux de conversion n’ont pas pu être chargés.
            </p>
          ) : null}

          {funnel.data ? (
            <FunnelPanel stats={funnel.data} rangeLabel={rangeInfo.label} />
          ) : funnel.isError ? (
            <p className="text-sm text-red-600">Le parcours n’a pas pu être chargé.</p>
          ) : null}

          <section aria-label="Pages les plus vues">
            <h2 className="font-display text-ink-900 mb-3 text-lg font-bold">
              Pages les plus vues · {rangeInfo.label}
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TopPages title="Site" app="site" pages={data.top_site} />
              <TopPages title="App" app="app" pages={data.top_app} />
            </div>
          </section>

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2" aria-label="Origine">
            <div className="border-ink-200 rounded-2xl border bg-white p-4">
              <h3 className="font-display text-ink-900 mb-3 text-sm font-bold">
                Visiteurs par support · {rangeInfo.label}
              </h3>
              <dl className="grid grid-cols-2 gap-3">
                {PLATFORM_LABELS.map((p) => (
                  <div key={p.key}>
                    <dt className="text-ink-500 text-xs">{p.label}</dt>
                    <dd className="font-display text-ink-900 text-lg font-bold tabular-nums">
                      {formatMetric('visitors', data.platforms[p.key] ?? 0)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="border-ink-200 rounded-2xl border bg-white p-4">
              <h3 className="font-display text-ink-900 mb-1 text-sm font-bold">
                Robots écartés · {rangeInfo.label}
              </h3>
              <p className="text-ink-400 mb-3 text-xs">
                Pages vues par des robots : comptées à part, jamais dans les chiffres.
              </p>
              {botRows(data.bots).length === 0 ? (
                <p className="text-ink-400 text-sm">Aucun robot sur la période.</p>
              ) : (
                <ul className="flex flex-col gap-1 text-sm">
                  {botRows(data.bots).map((b) => (
                    <li key={b.label} className="flex justify-between gap-2">
                      <span className="text-ink-700">{b.label}</span>
                      <span className="text-ink-900 font-semibold tabular-nums">
                        {formatMetric('views', b.views)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
